import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getPayments } from '@/features/payments/api';
import { PaymentReceiptSheet } from '@/features/payments/payment-receipt-sheet';
import {
  FILTERABLE_PAYMENT_METHODS,
  PaymentMethod,
  PaymentType,
} from '@/features/payments/payment';
import type {
  PaymentListItemModel,
  PaymentMethodType,
  PaymentStatusT,
} from '@/features/payments/payment';
import {
  paymentTypeBadgeClass,
  paymentTypeLabel,
} from '@/features/payments/payment-display';
import { PaymentTypeFilter } from '@/features/payments/payment-type-filter';
import { ZaadCorrectionFilterSchema } from '@/features/payments/reversal';
import { ZaadCorrectionStatus } from '@/features/payments/components/ReversalDetails';
import { ResolveZaadCorrectionDialog } from '@/features/payments/components/ResolveZaadCorrectionDialog';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/ui/copyable-id';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  CreditCard,
  Plus,
  DollarSign,
  TrendingUp,
  Hash,
  Banknote,
  Receipt,
  AlertTriangle,
} from '@/lib/icons';
import { cn, formatCurrency, truncateId } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import {
  BranchScopeFilter,
  ListFilterBar,
  NoFilterResults,
} from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { RelativeTime } from '@/components/common/relative-time';
import { OdooLinkBadge } from '@/features/odoo/components/OdooLinkBadge';

/**
 * Chips the method filter offers — the live rails plus the hand-recorded
 * methods, straight off the rail catalog so this cannot drift from what the
 * Record Payment form and the push dialog accept.
 *
 * Rows on a withdrawn rail still *render* their own badge and label (see
 * METHOD_STYLES and the `payments.method.*` keys, which keep every value) —
 * they are simply not offered as something to filter by. The URL `status`
 * param stays wide for the same reason: a bookmarked or shared link that
 * filters on a withdrawn rail must keep working rather than fail validation
 * and drop the operator on an unfiltered list.
 */
const METHOD_VALUES = FILTERABLE_PAYMENT_METHODS;

const paymentsSearchSchema = z.object({
  q: z.string().optional(),
  /** Repeatable — an agent's book spans every branch they are assigned to. */
  branchIds: z.array(z.string()).optional(),
  status: PaymentMethod.optional(),
  // Straight off the domain enum rather than a parallel list — the copy that
  // lived here could drift from the schema the filter control reads.
  type: PaymentType.optional(),
  /** Reversed payments ZaadCredit still has to cancel, or already did. */
  zaadCorrection: ZaadCorrectionFilterSchema.optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/payments/')({
  validateSearch: paymentsSearchSchema,
  component: PaymentsPage,
});

/** Method pill colours — one hue per rail so operators can scan by channel. */
const METHOD_STYLES: Record<string, string> = {
  mobile_money:
    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  evc_plus:
    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  zaad: 'bg-teal-500/15 text-teal-600 border-teal-500/30 dark:text-teal-400',
  sahal: 'bg-cyan-500/15 text-cyan-600 border-cyan-500/30 dark:text-cyan-400',
  mpesa:
    'bg-green-500/15 text-green-600 border-green-500/30 dark:text-green-400',
  cash: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
  bank_transfer:
    'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
  auto_debit:
    'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
};

/** Mirrors the receipt sheet's palette so a row and its receipt agree. */
const STATUS_STYLES: Record<PaymentStatusT, string> = {
  completed:
    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  pending:
    'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
  failed: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
  reversed:
    'bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400',
};

const MOBILE_MONEY_METHODS = [
  'mobile_money',
  'evc_plus',
  'zaad',
  'sahal',
  'mpesa',
];

const PAGE_SIZE = 20;

/** Rows pulled per load — see the query below for why this is raised. */
const LIST_LIMIT = 500;

function PaymentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [receiptPayment, setReceiptPayment] =
    useState<PaymentListItemModel | null>(null);
  const [resolveTarget, setResolveTarget] =
    useState<PaymentListItemModel | null>(null);
  const { hasPermission } = useAuth();
  const canResolveCorrections = hasPermission('payments', 'reverse');
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search, to: '/payments' });

  // Search, method and branch narrow server-side; only paging is local, so the
  // page still pulls a raised limit. The endpoint's own default caps at 100,
  // which would drop rows out of the totals without any visible sign.
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      'payments',
      debouncedSearch,
      search.status,
      search.type,
      search.branchIds,
      search.zaadCorrection,
      LIST_LIMIT,
    ],
    queryFn: () =>
      getPayments({
        search: debouncedSearch,
        method: search.status,
        type: search.type,
        branchIds: search.branchIds,
        zaadCorrection: search.zaadCorrection,
        limit: LIST_LIMIT,
      }),
    placeholderData: (previous) => previous,
  });

  // Open corrections are few, so the chip's count is one small extra request.
  const { data: openCorrections } = useQuery({
    queryKey: ['payments', 'zaad-correction-open', search.branchIds, LIST_LIMIT],
    queryFn: () =>
      getPayments({
        zaadCorrection: 'open',
        branchIds: search.branchIds,
        limit: LIST_LIMIT,
      }),
    staleTime: 60_000,
  });
  const openCorrectionCount = openCorrections?.value?.length;
  const correctionFilterOn = search.zaadCorrection === 'open';
  // The shared hook counts the facets every list has; this page owns three more.
  const totalActiveFilters =
    activeCount +
    (search.branchIds?.length ? 1 : 0) +
    (search.type ? 1 : 0) +
    (search.zaadCorrection ? 1 : 0);

  // Every filter is applied by the server now, so these are the same rows —
  // the stat tiles describe exactly what the table below them holds.
  const allPayments = data?.value ?? [];
  const page = Math.min(
    search.page ?? 1,
    Math.max(1, Math.ceil(allPayments.length / PAGE_SIZE))
  );

  const totalPages = Math.max(1, Math.ceil(allPayments.length / PAGE_SIZE));
  const filtered = allPayments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const startItem = allPayments.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, allPayments.length);

  // Only money actually collected: pending, failed and reversed rows are not cash in hand.
  const collected = allPayments.filter((p) => p.status === 'completed');
  const totalCollected = collected.reduce((s, p) => s + p.amount, 0);
  const avgPayment =
    collected.length > 0 ? totalCollected / collected.length : 0;
  const mobileMoney = allPayments.filter((p) =>
    MOBILE_MONEY_METHODS.includes(p.method)
  ).length;

  const stats = [
    {
      label: t('payments.stats.totalCollected'),
      value: formatCurrency(totalCollected, currency),
      icon: DollarSign,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: t('payments.stats.totalPayments'),
      value: allPayments.length,
      icon: Hash,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: t('payments.stats.avgPayment'),
      value: formatCurrency(avgPayment, currency),
      icon: TrendingUp,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: t('payments.stats.mobileMoney'),
      value: mobileMoney,
      icon: Banknote,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  const methodChips: ReadonlyArray<FilterChip<'all' | PaymentMethodType>> = [
    { value: 'all', label: t('payments.filters.all') },
    ...METHOD_VALUES.map((value) => ({
      value,
      label: t(`payments.method.${value}`),
    })),
  ];

  /**
   * Step through to the contract that owns a payment, deep-linking its Payment
   * History to this exact row (`?highlight=`) so the operator lands on the
   * transaction they clicked instead of scanning the ledger for it.
   */
  const openContract = (payment: PaymentListItemModel) => {
    if (!payment.contractId) return;
    navigate({
      to: '/contracts/$contractId',
      params: { contractId: payment.contractId },
      search: { highlight: payment.id },
    });
  };

  const renderRow = (p: PaymentListItemModel) => {
    const linked = !!p.contractId;
    // A settlement that did not complete is the row an operator needs to act
    // on, so it carries its own pill; completed rows stay quiet.
    const showStatus = p.status !== 'completed';
    const settled = p.status === 'completed' || p.status === 'pending';
    const rowLabel = t('payments.openContractFor', {
      customer: p.customerName || t('payments.unknownCustomer'),
      amount: formatCurrency(p.amount, currency),
    });

    return (
      <TableRow
        key={p.id}
        // Mouse convenience only — the contract link in the Contract cell is
        // the real keyboard/assistive-tech affordance, so the row itself stays
        // a plain (non-focusable) row and table semantics are preserved.
        onClick={
          linked
            ? (e) => {
                // Let the nested link / copy / receipt controls win.
                if ((e.target as HTMLElement).closest('a,button')) return;
                openContract(p);
              }
            : undefined
        }
        className={cn(
          'group transition-colors row-hover-lift',
          linked ? 'cursor-pointer hover:bg-accent/30' : 'hover:bg-muted/20'
        )}
      >
        <TableCell className="font-medium">
          {p.customerName || (
            <span className="text-muted-foreground">
              {t('payments.unknownCustomer')}
            </span>
          )}
        </TableCell>

        <TableCell>
          {linked ? (
            // Link and copy button are siblings, not nested — an <a> may not
            // contain a <button>.
            <span className="group/id inline-flex items-center gap-1.5">
              <Link
                to="/contracts/$contractId"
                params={{ contractId: p.contractId as string }}
                search={{ highlight: p.id }}
                title={p.contractNumber}
                aria-label={rowLabel}
                className="rounded-sm font-mono text-sm tracking-tight text-foreground transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background group-hover:text-primary"
              >
                {truncateId(p.contractNumber)}
              </Link>
              <CopyButton
                value={p.contractNumber}
                label={t('payments.receipt.copyContractId')}
                copiedLabel={t('common.copied')}
              />
            </span>
          ) : p.applicationId ? (
            // Upfront money collected before the contract existed. It has an
            // application, not a contract — naming it beats a blank cell.
            <span
              className="font-mono text-sm tracking-tight text-muted-foreground"
              title={p.applicationId}
            >
              {t('payments.viaApplication', {
                id: truncateId(p.applicationId),
              })}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t('payments.unlinked')}
            </span>
          )}
        </TableCell>

        {/* Always rendered. Hidden below `lg` this vanished on every laptop
            under 1024px, taking with it the one thing that tells a deposit
            apart from an installment — the card below scrolls horizontally,
            so the column costs nothing on a phone. */}
        <TableCell>
          <Badge
            className={cn(
              'whitespace-nowrap text-[10px]',
              paymentTypeBadgeClass(p.type)
            )}
          >
            {paymentTypeLabel(t, p.type)}
          </Badge>
        </TableCell>

        <TableCell>
          <span
            className={cn(
              'font-semibold',
              settled
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-muted-foreground line-through'
            )}
          >
            {formatCurrency(p.amount, currency)}
          </span>
          {showStatus && (
            <Badge
              className={cn(
                'ml-2 align-middle text-[10px]',
                STATUS_STYLES[p.status] ?? STATUS_STYLES.pending
              )}
            >
              {t(`payments.status.${p.status}`)}
            </Badge>
          )}
          <OdooLinkBadge
            subjectType="payment"
            subjectId={p.id}
            className="ml-2 align-middle"
          />
          <ZaadCorrectionStatus
            payment={p}
            className="mt-1 max-w-[36ch]"
            action={
              canResolveCorrections && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[11px]"
                  onClick={() => setResolveTarget(p)}
                  aria-label={t('payments.zaadCorrection.markCorrectedFor', {
                    customer: p.customerName || t('payments.unknownCustomer'),
                    amount: formatCurrency(p.amount, currency),
                  })}
                >
                  {t('payments.zaadCorrection.markCorrected')}
                </Button>
              )
            }
          />
        </TableCell>

        <TableCell className="hidden sm:table-cell">
          <Badge className={METHOD_STYLES[p.method] ?? ''}>
            {t(`payments.method.${p.method}`, { defaultValue: p.method })}
          </Badge>
        </TableCell>

        <TableCell className="hidden md:table-cell">
          {p.providerTransactionId ? (
            <span className="group/id inline-flex items-center gap-1.5">
              <span
                className="font-mono text-xs text-muted-foreground"
                title={p.providerTransactionId}
              >
                {truncateId(p.providerTransactionId, 12, 6)}
              </span>
              <CopyButton
                value={p.providerTransactionId}
                label={t('payments.receipt.copyReference')}
                copiedLabel={t('common.copied')}
              />
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>

        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
          <RelativeTime value={p.createdAt} />
        </TableCell>

        <TableCell className="w-10 p-0 pr-3 text-right">
          <button
            type="button"
            onClick={() => setReceiptPayment(p)}
            aria-label={t('payments.receipt.openFor', {
              customer: p.customerName || t('payments.unknownCustomer'),
              amount: formatCurrency(p.amount, currency),
            })}
            title={t('payments.receipt.title')}
            // Always visible on touch-sized viewports, where a hover-reveal
            // would leave it unreachable.
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-all duration-150 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background md:opacity-0 md:group-hover:opacity-100"
          >
            <Receipt className="h-4 w-4" />
          </button>
        </TableCell>
      </TableRow>
    );
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <Card className="shadow-md border-0">
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      );
    }

    if (isError) {
      return (
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title={t('payments.loadError')}
        />
      );
    }

    if (allPayments.length === 0) {
      return (
        <NoFilterResults
          isFiltered={totalActiveFilters > 0}
          emptyTitle={t('payments.empty.title')}
          emptyDescription={t('payments.empty.description')}
          icon={CreditCard}
          onReset={reset}
          action={
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/contracts' })}
            >
              {t('payments.empty.openContracts')}
            </Button>
          }
        />
      );
    }

    return (
      <Card className="shadow-md border-0">
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>{t('payments.columns.customer')}</TableHead>
                <TableHead>{t('payments.columns.contract')}</TableHead>
                <TableHead>{t('payments.columns.type')}</TableHead>
                <TableHead>{t('payments.columns.amount')}</TableHead>
                <TableHead className="hidden sm:table-cell">
                  {t('payments.columns.method')}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {t('payments.columns.reference')}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {t('payments.columns.date')}
                </TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">{t('payments.receipt.title')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{filtered.map(renderRow)}</TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('payments.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('payments.description')}
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
          onClick={() => navigate({ to: '/payments/record' })}
        >
          <Plus className="w-4 h-4 mr-2" /> {t('payments.recordPayment')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-in">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md card-shine"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar
        chips={methodChips}
        chipValue={search.status ?? 'all'}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        chipsAriaLabel={t('payments.columns.method')}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.payments')}
        isSearchPending={isSearchPending}
        activeCount={totalActiveFilters}
        onReset={reset}
        isPending={isFetching}
      >
        {/* Rendered as extra facets rather than through the bar's own
            single-branch slot, so the six other lists that share it are
            untouched. Method already owns the chip row above, so kind-of-money
            takes a select here — the axis an operator reconciling deposits
            against installments actually needs. */}
        <BranchScopeFilter
          value={search.branchIds}
          onChange={(branchIds) => setFilters({ branchIds })}
        />
        <PaymentTypeFilter
          value={search.type}
          onChange={(type) => setFilters({ type })}
        />
        <Button
          type="button"
          variant={correctionFilterOn ? 'default' : 'outline'}
          className="h-10 shrink-0 gap-1.5 px-3"
          aria-pressed={correctionFilterOn}
          aria-label={t('payments.zaadCorrection.filterWithCount', {
            count: openCorrectionCount ?? 0,
          })}
          title={t('payments.zaadCorrection.filter')}
          onClick={() =>
            setFilters({ zaadCorrection: correctionFilterOn ? undefined : 'open' })
          }
        >
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <span className="hidden lg:inline">
            {t('payments.zaadCorrection.filter')}
          </span>
          {openCorrectionCount !== undefined && openCorrectionCount > 0 && (
            <Badge
              className={cn(
                'h-5 min-w-5 justify-center px-1.5 text-[10px] tabular-nums',
                correctionFilterOn
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-400'
              )}
            >
              {openCorrectionCount}
            </Badge>
          )}
        </Button>
      </ListFilterBar>

      {renderResults()}

      {/* Pagination */}
      {allPayments.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('payments.showing', {
              start: startItem,
              end: endItem,
              total: allPayments.length,
            })}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setFilters({ page: i + 1 })}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setFilters({ page: Math.min(totalPages, page + 1) })
                  }
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Receipt drawer. Kept mounted so the close animation can play out; the
          detail query is gated on `open`. */}
      <PaymentReceiptSheet
        paymentId={receiptPayment?.id ?? null}
        open={!!receiptPayment}
        onOpenChange={(open) => {
          if (!open) setReceiptPayment(null);
        }}
        fallbackCustomerName={receiptPayment?.customerName}
        fallbackContractId={receiptPayment?.contractId ?? undefined}
      />
      {resolveTarget && (
        <ResolveZaadCorrectionDialog
          open
          onOpenChange={(open) => {
            if (!open) setResolveTarget(null);
          }}
          payment={resolveTarget}
          currency={currency}
        />
      )}
    </div>
  );
}
